import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Pagination,
  PaginationItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import Link from 'components/MUI/Link';
import ShowingPage from 'components/shared/ShowingPage';
import TextSearchField from 'components/shared/TextSearchField';
import { getPerformanceLabPagePaths } from 'helpers/getPerformanceLabPages';
import { InferTRPCOutputTypes, trpc } from 'helpers/trpc';

type PlayerSearchItem =
  InferTRPCOutputTypes['recruitmentCenter']['getPlayersSearch']['players'][number];

interface Props {
  open: boolean;
  handleClose: () => void;
}

const playersPerPage = 5;
const skeletonButtonCount = 8;
const defaultSearchValue = '';
const initialPage = 1;
const defaultPlayerCount = 0;
const initialOffset = 0;
const defaultLastPage = 0;

const customStyles = {
  titleStack: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    pr: 3,
  },
  filteredPlayersStack: {
    px: 1,
  },
  filteredPlayersInnerStack: {
    py: 3,
    alignItems: 'center',
  },
  playerName: {
    wordWrap: 'break-word',
    fontSize: 16,
  },
  paginationStack: {
    px: 6,
    pt: 2,
    pb: 4,
    width: '90%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    bottom: 12,
  },
  pagination: {
    mt: 2,
  },
  mainBox: {
    width: 600,
    overflowX: 'hidden',
    height: 600,
  },
  avatarSkeleton: {
    width: 48,
    height: 48,
  },
  nameSkeleton: {
    width: '15vw',
    height: 25,
  },
  playerSkeletonStack: {
    px: 1,
  },
  playerSkeletonInnerStack: {
    py: 3,
    alignItems: 'center',
  },
  playerCountBox: {
    mt: 2,
    pl: 0,
  },
  playerCountSkeleton: {
    width: 160,
  },
  playerLink: {
    textDecoration: 'none',
  },
  playerRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  skeletonArrow: {
    width: 25,
    height: 25,
  },
  paginationSkeletonText: {
    width: '30%',
    height: 30,
  },
  paginationNumbersSkeleton: {
    mt: 2,
  },
  link: {
    textDecoration: 'none',
    width: '100%',
  },
  search: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      background: '#F6F6F6',
    },
  },
  icon: {
    color: 'black',
  },
};

const PlayerSearchModal = ({ open, handleClose }: Props) => {
  const router = useRouter();
  const [page, setPage] = useState(initialPage);
  const [searchTerm, setSearchTerm] = useState(defaultSearchValue);
  const [playersCount, setPlayersCount] = useState(defaultPlayerCount);
  const [selectedPagePlayers, setSelectedPagePlayers] = useState<
    PlayerSearchItem[]
  >([]);

  const offset = useRef(initialOffset);
  const searchTermLength = useRef(searchTerm.length);

  const isPerformanceLabPage = getPerformanceLabPagePaths().includes(
    router.pathname
  );

  const iconStyle = isPerformanceLabPage ? {} : customStyles.icon;

  const {
    data: players,
    refetch: refetchPlayers,
    isFetching: isPlayersFetching,
  } = trpc.recruitmentCenter.getPlayersSearch.useQuery(
    { playerName: searchTerm },
    { enabled: false }
  );

  const onClose = () => {
    setPage(initialPage);
    setSearchTerm(defaultSearchValue);
    searchTermLength.current = defaultSearchValue.length;
    handleClose();
  };

  const searchPlayersByName = async (searchValue: string) => {
    setSearchTerm(searchValue);
    setPage(initialPage);

    searchTermLength.current = searchValue.length;
  };

  const setPageData = (pageNumber: number) => {
    setPage(pageNumber);

    const offset = (pageNumber - 1) * playersPerPage;

    if (players) {
      setSelectedPagePlayers(
        players.players.slice(offset, playersPerPage + offset)
      );
    }
  };

  const lastPage = playersCount
    ? Math.ceil(playersCount / playersPerPage)
    : defaultLastPage;

  useEffect(() => {
    if (searchTermLength.current > 0) {
      refetchPlayers();
    }
  }, [refetchPlayers, searchTerm]);

  useEffect(() => {
    if (players) {
      setPlayersCount(players.players?.length);

      setSelectedPagePlayers(
        players.players.slice(offset.current, playersPerPage + offset.current)
      );
    }
  }, [players]);

  const paginationSkeleton = (
    <Stack sx={customStyles.paginationStack}>
      <Skeleton variant="text" sx={customStyles.paginationSkeletonText} />

      <Stack
        direction="row"
        spacing={2}
        sx={customStyles.paginationNumbersSkeleton}
      >
        {Array.from({ length: skeletonButtonCount }).map((_, index) => (
          <Skeleton
            key={index}
            variant="circular"
            sx={customStyles.skeletonArrow}
          />
        ))}
      </Stack>
    </Stack>
  );

  const titleSection = (
    <DialogTitle sx={customStyles.titleStack}>
      {'Player Search'}

      <IconButton onClick={onClose}>
        <CloseIcon sx={iconStyle} />
      </IconButton>
    </DialogTitle>
  );

  const playerSearchSection = (
    <>
      <TextSearchField
        label="Search"
        variant="outlined"
        fullWidth
        onSearchChange={(text) => searchPlayersByName(text)}
        size="small"
        waitLimit={300}
        sx={isPerformanceLabPage ? {} : customStyles.search}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <SearchIcon sx={iconStyle} />
            </InputAdornment>
          ),
        }}
        autoFocus
      />

      <Box sx={customStyles.playerCountBox}>
        {isPlayersFetching ? (
          <Skeleton variant="text" sx={customStyles.playerCountSkeleton} />
        ) : (
          <Typography>
            {`${
              searchTermLength.current && playersCount
                ? playersCount
                : defaultPlayerCount
            } Players found`}
          </Typography>
        )}
      </Box>
    </>
  );

  const filteredPlayersSection = (
    <>
      {searchTermLength.current > 0 && (
        <Stack divider={<Divider />} sx={customStyles.filteredPlayersStack}>
          {selectedPagePlayers.map((player, index) => (
            <Link
              key={index}
              href={`playerprofile?playerId=${player.id}`}
              sx={(theme) => ({
                ...customStyles.link,
                color: theme.palette.mode === 'dark' ? 'white' : 'black',
              })}
              target="_blank"
              rel="noreferrer"
            >
              <Stack direction="row" sx={customStyles.playerRow}>
                <Stack
                  direction="row"
                  spacing={4}
                  key={index}
                  sx={customStyles.filteredPlayersInnerStack}
                >
                  <Avatar src={player.profileImagePath} />

                  <Typography variant="h3" sx={customStyles.playerName}>
                    {player.name}
                  </Typography>
                </Stack>

                <IconButton>
                  <KeyboardArrowRightIcon sx={iconStyle} />
                </IconButton>
              </Stack>
            </Link>
          ))}
        </Stack>
      )}
    </>
  );

  const paginationSection = (
    <>
      {searchTermLength.current > 0 && (
        <Stack sx={customStyles.paginationStack}>
          <ShowingPage page={page} lastPage={lastPage} />

          <Pagination
            sx={customStyles.pagination}
            count={lastPage}
            color="primary"
            page={page}
            renderItem={(item) => (
              <PaginationItem
                components={{
                  previous: KeyboardArrowLeftIcon,
                  next: KeyboardArrowRightIcon,
                }}
                {...item}
              />
            )}
            onChange={(_event, value) => setPageData(value)}
          />
        </Stack>
      )}
    </>
  );

  const playerListSkeleton = (
    <Stack divider={<Divider />} sx={customStyles.playerSkeletonStack}>
      {Array.from({ length: playersPerPage }).map((_, index) => (
        <Stack key={index} direction="row" sx={customStyles.playerRow}>
          <Stack
            direction="row"
            spacing={4}
            sx={customStyles.playerSkeletonInnerStack}
          >
            <Skeleton variant="circular" sx={customStyles.avatarSkeleton} />

            <Skeleton variant="text" sx={customStyles.nameSkeleton} />
          </Stack>

          <Skeleton variant="circular" sx={customStyles.skeletonArrow} />
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <Box sx={customStyles.mainBox}>
        {titleSection}

        <DialogContent>
          {playerSearchSection}

          {isPlayersFetching ? playerListSkeleton : filteredPlayersSection}

          {isPlayersFetching
            ? paginationSkeleton
            : players && players.players.length > 0 && paginationSection}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default PlayerSearchModal;
